<template>
  <SendModal
    @close="requestCloseModal"
    @clickBackdrop="requestCloseModal"
    disableBackdrop
  >
    <div :class="$style.wrapper">
      <div :class="$style.header">
        <h2>チャットメッセージの一括送信</h2>
        <ButtonClose
          v-if="!isSendRequesting"
          :class="[$style.header__close, $style.close]"
          @click="requestCloseModal"
        />
      </div>
      <p :class="$style['wrapper__description']">
        メッセージを {{ targetNames }} の{{ targetCount }}名に送ります
      </p>
      <ChatPageAction
        :is-line-blocked="false"
        :class="$style['wrapper__action']"
        v-model:text="message"
        v-model:selections="selections"
        v-model:sendAsStaff="sendAsStaff"
        :isSending="isSendRequesting"
        :showSendAsStaffButton="hasLineActivationProvider"
        @send="handleSendMessage"
        @changeInput="handleChangeInput"
      />
    </div>
  </SendModal>
</template>

<script setup lang="ts">
import type { Match } from '@/types/app/match'
import { computed, ref, watch } from 'vue'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { useModal, useModalState } from '@/hooks/useModal'
import ChatPageAction from '../../ChatPageOwnView/ChatPageAction.vue'
import { useAlert } from '@/libs/alertPortal'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import {
  useSendNormalChatMessagesMutation,
  useSendQuestionChatMessagesMutation,
} from './BulkMessageSendModal.generated'

const props = defineProps<{
  matches: Match[]
  open: boolean
  hasLineActivationProvider: boolean
}>()

const emit = defineEmits<{
  (event: 'sended'): void
  (event: 'close'): void
}>()

const hasInput = ref(false)
const isSendRequesting = ref(false)
const sendAsStaff = ref(false)

const handleChangeInput = (_hasInput: boolean) => {
  hasInput.value = _hasInput
}

const requestCloseModal = () => {
  if (isSendRequesting.value) {
    return
  }

  if (
    hasInput.value &&
    !window.confirm('入力中の内容は破棄されます。よろしいですか？')
  ) {
    return
  }
  emit('close')
}

const { closeSendModal, openSendModal, SendModal } = useModal('Send')

const {
  openModal: openLoadingScreenModal,
  closeModal: closeLoadingScreenModal,
} = useModalState()

const message = ref('')
const selections = ref<string[]>([])

watch(
  () => props.open,
  val => {
    if (val) {
      openSendModal()
    } else {
      message.value = ''
      selections.value = []
      closeSendModal()
    }
  },
  { immediate: true }
)

const alert = useAlert()

const sendNormalChatMessagesMutation = useSendNormalChatMessagesMutation()
const sendQuestionChatMessagesMutation = useSendQuestionChatMessagesMutation()

const handleSendMessage = async () => {
  if (!window.confirm('この内容で送信しますか？')) {
    return
  }

  openLoadingScreenModal()
  isSendRequesting.value = true

  const _message = message.value
  const _selections = selections.value

  message.value = ''
  selections.value = []

  try {
    if (_selections.length > 0) {
      await sendQuestionChatMessagesMutation.mutate({
        input: {
          chatRoomIds: props.matches.map(({ roomId }) => roomId.toString()),
          message: _message,
          answerSelections: _selections,
          sendAsStaff: sendAsStaff.value,
        },
      })
    } else {
      await sendNormalChatMessagesMutation.mutate({
        input: {
          chatRoomIds: props.matches.map(({ roomId }) => roomId.toString()),
          message: _message,
          sendAsStaff: sendAsStaff.value,
        },
      })
    }

    emit('sended')
  } catch (e) {
    message.value = _message
    selections.value = _selections

    console.error(e)
    alert.error(
      'エラーが発生しました。時間を置いて画面を読み込み直してください'
    )
    newrelicCaptureException(e)
  } finally {
    isSendRequesting.value = false
    closeLoadingScreenModal()
  }
}

const targetNames = computed(() => {
  return props.matches.map(match => match.user.nickname).join('、')
})

const targetCount = computed(() => {
  return props.matches.length
})
</script>

<style module lang="scss">
.wrapper {
  padding: 16px;
  min-width: 600px;

  &__description {
    margin-top: 16px;
  }

  &__action {
    margin-top: 32px;
  }
}

.header {
  display: flex;
  justify-content: space-between;
}
</style>
